import React, { useCallback, useState } from 'react'
import { Flex, Infobox, Modal, Typography, sendNotification } from 'components'
import {
  WebsocketConsumer,
  WebsocketMessage,
  WebsocketResultModalMessage,
} from './WebsocketConsumer'
import { useDispatch, useSelector } from 'react-redux'
import { reportsApi } from 'services/ReportManagement/reports'
import { useTranslation } from 'utils/useTranslation'
import { BulkActionToastContent } from 'containers/BulkActionToastContent/BulkActionToastContent'
import { LayoutDetailItem } from 'containers/Layout'
import { caseApi } from 'services/Engagement'
import { selectCurrentUser } from 'services/slices/auth'
import { usePermission } from 'services/slices/PermissionProvider'
import { PERMISSIONS } from 'services/auth/permissions'
import { setUnreadNotificationsCount } from 'services/slices/ui'

type WebsocketNotificationProps = {
  url: string
}

export const WebsocketNotification: React.FC<
  React.PropsWithChildren<WebsocketNotificationProps>
> = ({ url }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [resultModal, setResultModalState] = useState<
    WebsocketResultModalMessage['data'] & { isOpen: boolean }
  >()

  const { hasPermission } = usePermission()

  const user = useSelector(selectCurrentUser)
  const userId = user?.user_id

  const closeResultModal = () => setResultModalState(undefined)

  const onMessage = useCallback(
    (message: WebsocketMessage) => {
      const { type, data } = message

      if (type === 'toast') {
        const { description, link, title, type: notificationType } = data
        sendNotification({
          type: notificationType,
          header: title,
          toastContent: description,
          toastOptions: { toastId: crypto.randomUUID().toString(), autoClose: false },
          hyperLink: link
            ? {
                type: link.target === '_blank' ? 'newTab' : 'currentTab',
                label: link.label,
                to:
                  link.value === '/reports/0' && data.export_id
                    ? `${link.value}?exportId=${data.export_id}`
                    : link.value,
                isExternalLink: false,
              }
            : undefined,
        })

        dispatch(reportsApi.util.invalidateTags(['ExportReports', 'ImportDetail', 'ImportReports']))
      } else if (type === 'result-modal') {
        setResultModalState({
          ...data,
          isOpen: true,
        })
      } else if (type === 'bulk_action_toast') {
        let detailLinkRoute = undefined
        switch (data.model) {
          case 'entity':
            detailLinkRoute = '/engagement/entities/detail/'
            break

          case 'service_point':
            detailLinkRoute = '/engagement/service_points/detail/'
            break

          case 'ops_service_point':
            detailLinkRoute = '/ops_management/planning/service_points/detail/'
            break

          default:
            break
        }

        sendNotification({
          type: data.type,
          header: data.title,
          toastContent: (
            <BulkActionToastContent
              failure_list={data.failure_list}
              success_list={data.success_list}
              detailLinkRoute={detailLinkRoute}
              toastTranslationKey="common"
              message={data.description}
            />
          ),
          toastOptions: { toastId: crypto.randomUUID().toString(), autoClose: false },
        })
      } else if (type === 'created_object_details_toast') {
        if (data?.creator_id !== userId) {
          const { details, title, type: notificationType, link, model } = data

          if (model === 'case' && !hasPermission(PERMISSIONS.ENGAGEMENT.CASE.VIEW)) return

          sendNotification({
            type: notificationType,
            header: t(title),
            toastContent: (
              <Flex directionColumn="zero" gap={0.5}>
                {details.map((detail, id) => (
                  <LayoutDetailItem
                    key={id}
                    title={t(detail.label)}
                    type={detail.type}
                    value={
                      typeof detail?.value === 'number' ? detail?.value.toString() : detail?.value
                    }
                    noWrap={false}
                    wordBreak="break-word"
                  />
                ))}
              </Flex>
            ),
            toastOptions: { toastId: crypto.randomUUID().toString(), autoClose: false },
            hyperLink: link
              ? {
                  type: link.target === '_blank' ? 'newTab' : 'currentTab',
                  label: t(link.label),
                  to: link.value,
                  isExternalLink: false,
                }
              : undefined,
          })

          switch (model) {
            case 'case':
              dispatch(caseApi.util.invalidateTags(['CaseDetail', 'Cases']))
              break

            default:
              break
          }
        }
      } else if (type === 'unread_notifications_counts') {
        dispatch(caseApi.util.invalidateTags(['Cases']))
        dispatch(caseApi.util.invalidateTags(['Comments']))

        dispatch(setUnreadNotificationsCount(data))
      }
    },
    [userId, dispatch],
  )

  return (
    <WebsocketConsumer url={url} onMessage={onMessage}>
      {resultModal && (
        <Modal
          data-testid="result-modal"
          heading={resultModal?.title}
          isOpen={resultModal?.isOpen}
          noCancel
          onSubmit={closeResultModal}
          customSubmitText={t('okay')}
          onRequestClose={closeResultModal}
        >
          <Flex directionColumn="zero" gap={0.5}>
            {resultModal?.success_list?.length > 0 && (
              <Infobox variant="success" text={resultModal?.success_message} hideClose width="100%">
                {resultModal?.success_list.map(({ message, value }, i) => (
                  <Typography
                    variant="span"
                    fontSize="text_sm"
                    fontWeight="regular"
                    color="green600"
                    key={i}
                  >
                    <b style={{ fontWeight: '600' }}>{value}</b> {message}
                  </Typography>
                ))}
              </Infobox>
            )}
            {resultModal?.failure_list?.length > 0 && (
              <Infobox variant="error" text={resultModal?.failure_message} hideClose width="100%">
                {resultModal?.failure_list?.map(({ reason, value }, i) => (
                  <Typography
                    variant="span"
                    fontSize="text_sm"
                    fontWeight="regular"
                    color="red600"
                    key={i}
                  >
                    <b style={{ fontWeight: '600' }}>{value}</b> {reason}
                  </Typography>
                ))}
              </Infobox>
            )}
          </Flex>
        </Modal>
      )}
    </WebsocketConsumer>
  )
}
